/**
 * Lieferanten: Namensnormalisierung, Kennzeichen, Ratenvorlagen-Zusammenfassung und die Zuordnung
 * Bestellung -> Lieferant - 1:1-Portierung aus `index.html` (Stand 2026-08-08). Die Rechenwege sind
 * woertlich uebernommen, auch wo sie umstaendlich wirken oder auf den ersten Blick fehlerhaft
 * aussehen; ergaenzt wurden ausschliesslich Typen. Namen und Logik sind unangetastet.
 *
 * ================= Nicht portiert: die HTML-erzeugenden Helfer =================
 *
 * `supplierOptions` (index.html:5014), `supplierSelectOptions` (5017), `supplierLogoImg` (4863),
 * `warehouseSelectOptions` (5029), `billingAddressHtml` (5033), `paymentTemplateOptions` (4866) und
 * `supplierQuickTemplateOptions` (4869) bauen Markup als Zeichenkette zusammen. Sie werden in React
 * durch JSX ersetzt und stehen laut Task-Brief ausdruecklich nicht auf der Portierungsliste.
 *
 * Ebenfalls nicht portiert, aber aus anderem Grund:
 * - `supplierLogo` (4855) haengt an `fileUrl` und damit an der Dateiablage, die die Backend-Spec
 *   ohnehin ersetzt. Einziger Aufrufer ist `supplierLogoImg` (4864), der oben schon entfaellt; der
 *   Rueckfallzweig `supplierBadgeUrl` ist portiert.
 * - `supplierNames` (5002) liest `DB.suppliers` UND `DB.orders` und steht nicht im Brief. ACHTUNG
 *   fuer Folgetasks: es hat VIER Aufrufer, nicht nur die Options-Helfer - `supplierOptions` (5015),
 *   `supplierSelectOptions` (5019), die Filter-Datalist (6444, Markup) und `supplierBoardColumns`
 *   (6662, innerhalb von 6648). `supplierBoardColumns` ist eine reine Datenfunktion (sie baut
 *   Board-Spalten auf und benutzt selbst `supplierKey` und `allSuppliers`) und faellt damit einem
 *   Folgetask zu - wer sie portiert, muss `supplierNames` mitnehmen.
 *
 * ================= Der Umbau: DB-Bindung -> Argument =================
 *
 * Im Original lesen `allSuppliers` (4839), `supplierByName` (5038) und `orderSupplier` (4759) das
 * globale `DB.suppliers`, `warehouseNames` (5021) das globale `DB.orders`. Hier bekommen sie die
 * jeweilige Liste als Argument. Das ist der einzige Eingriff in die Aufrufform - die Rechenwege
 * darin sind unveraendert. `orderCurrency` reicht die Lieferantenliste nur an `orderSupplier`
 * weiter.
 *
 * Nicht portiert sind die SCHREIBENDEN DB-Funktionen desselben Abschnitts (`ensureSupplierRecord`
 * 4698, `setOrderSupplierRecord` 4718, `syncSuppliersFromOrders` 4736): sie legen Datensaetze an
 * bzw. schreiben sie zurueck und gehoeren damit nicht in eine reine Berechnungsschicht. Sie stehen
 * auch nicht auf der Liste des Task-Briefs.
 *
 * ================= Woertlich uebernommene Eigenheiten =================
 *
 * 1. REPARIERT am 2026-08-11 (Befund 2, Option B) - siehe Kommentar an supplierKey. BEIDE
 *    Wurfstellen der Kette sind erfasst: `supplierKey` (Task 4) und die Sortierung in
 *    `allSuppliers` ueber den rohen Namen (nachgezogen am 2026-08-11, Gesamtreview Befund I7 -
 *    siehe Kommentar dort). Der Beschluss lautet „ueber die gesamte Aufrufkette".
 * 2. `PAYMENT_TEMPLATE_MAP[s.paymentTemplate || '']` (index.html:4997) ist ein ungesicherter
 *    Zugriff auf ein Objekt mit `Object.prototype` in der Kette: `paymentTemplate: "constructor"`
 *    greift durch und liefert `Object`. Hier ABSICHTLICH nicht per `Object.hasOwn` abgesichert -
 *    das waere eine Verhaltensaenderung in einer Phase, die woertliche Uebernahme verlangt. Nach
 *    aussen tritt der durchgegriffene Wert nicht: die anschliessende Pruefung `t && t.id` faellt
 *    bei jedem Prototyp-Wert auf `'Keine Vorlage'` zurueck. Die Tabelle selbst liegt in
 *    `src/config/payments.ts` und bleibt dort ein gewoehnliches `Object.fromEntries`-Objekt (kein
 *    `Object.create(null)`) - Begruendung dort.
 * 3. `supplierBadgeUrl` liest den Namens-Hash mit `ch.charCodeAt(0)` aus einer Schleife, die ueber
 *    CODEPUNKTE laeuft (`for (const ch of ...)`). Bei einem Zeichen ausserhalb der Basic
 *    Multilingual Plane (Emoji) ist `ch` ein Ersatzzeichenpaar, `charCodeAt(0)` liefert davon nur
 *    die erste Codeeinheit - der Rest des Zeichens geht nicht in den Hash ein. Woertlich
 *    uebernommen.
 * 4. `warehouseNames` filtert Lagerbezeichnungen ueber `String(order.warehouse || '').trim()`. Eine
 *    Zahl wird damit zur Bezeichnung ("42"), nicht verworfen. Woertlich uebernommen.
 * 5. `orderSupplier` liefert je nach Zweig UNTERSCHIEDLICH aufbereitete Datensaetze: ueber
 *    `supplierId` den ROHEN Eintrag aus der Liste, ueber den Namen die von `allSuppliers`
 *    aufbereitete Fassung (mit ergaenzten Rueckfallwerten und ohne Dopplungen). Woertlich
 *    uebernommen; siehe Bericht zu Task 4.
 *
 * ================= Notwendige Abhaengigkeiten =================
 *
 * `supplierById` (index.html:4693) steht nicht auf der Portierungsliste dieses Tasks, wird von ihr
 * aber gebraucht. Es ist hier modulintern und bewusst NICHT exportiert - dasselbe Vorgehen wie bei
 * `orderExcelCell` in `format.ts` und den Hilfsfunktionen in `status.ts`. Sobald ein Folgetask es
 * eigenstaendig portiert, ist diese Fassung durch einen Import zu ersetzen.
 *
 * `PAYMENT_TEMPLATE_MAP` (index.html:4564) wird ebenfalls gebraucht, liegt aber NICHT hier: es ist
 * eine aus `PAYMENT_TEMPLATES` abgeleitete Konstante und gehoert damit neben ihre Quelle nach
 * `src/config/payments.ts`. Sie ist dort in `test/helpers/configKonstanten.ts` als abgeleitete
 * Konstante angemeldet und wird wie die uebrigen gegen `index.html` differenzgetestet. Die
 * Lieferanten-Zusammenfassung liest sie hier. Neue Bestellungen leiten daraus keine
 * lokalen Raten mehr ab: Task 13 uebergibt die ausgewaehlte Freigabe an die Zahlungsziel-Route.
 */
import { PAYMENT_TEMPLATE_MAP } from "../config/payments.ts";
import { DEFAULT_WAREHOUSES } from "../config/warehouses.ts";
import { EUR, NUM } from "./format.ts";

// ================= Typen =================
//
// Die Typdeklarationen dieses Moduls stehen seit Task 1 gebuendelt in `lib/types.ts` (Begruendung
// dort: `Supplier` war hier und in `payments.ts` nur in einer Richtung zuweisbar). Re-Export unter
// den bisherigen Namen, damit kein bestehender Importpfad bricht.
import type { Supplier, SupplierPlanRow, Order } from "./types.ts";
export type { Supplier, SupplierPlanRow, Order };

// ================= Namen und Kennzeichen =================

/**
 * index.html:4696. ABWEICHUNG vom Original (Befund 2, Option B, entschieden 2026-08-11):
 * `String(name || '')` statt `(name || '')`. Das Original faellt bei einem WAHRHAFTIGEN
 * Nicht-String (42, true, [], {}) auf den Wert selbst zurueck und wirft an `.trim()`. Weil `Board`
 * fuer JEDE angezeigte Bestellung ueber `orderCurrency`/`allSuppliers` hier hereinlaeuft, riss eine
 * einzige fehlerhafte Bestellung das Rendern der GESAMTEN Liste ab - der grossflaechigste der
 * zwoelf Befunde.
 *
 * MITREPARIERT seit dem 2026-08-11 (Gesamtreview des Zweigs, Befund I7): `allSuppliers` sortierte
 * unten ueber `((a.name || '') as string).localeCompare(...)` auf dem ROHEN Namen und warf dort
 * weiter, sobald zwei solche Namen verglichen wurden - die Entscheidung „ueber die gesamte
 * Aufrufkette" war damit nur zur Haelfte umgesetzt. Begruendung an der Stelle selbst.
 */
export const supplierKey = (name: unknown): string => String(name || "").trim().toLowerCase();

/** index.html:4697 */
export const normalizeSupplierName = (name: unknown): string =>
  String(name || "").replace(/\s+/g, " ").trim();

/** index.html:4825 */
export function supplierInitials(name: unknown): string {
  const parts = String(name || "?")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  const raw = (
    parts.length > 1 ? parts[0][0] + parts[1][0] : (parts[0] || "?").slice(0, 2)
  ).toUpperCase();
  return raw.replace(/[^A-Z0-9ÄÖÜ]/g, "").slice(0, 2) || "?";
}

/** index.html:4830 */
export function supplierBadgeUrl(name: unknown): string {
  const label = supplierInitials(name);
  let hash = 0;
  for (const ch of String(name || "Lieferant")) hash = (hash * 31 + ch.charCodeAt(0)) % 360;
  const bg = `hsl(${hash}, 52%, 88%)`;
  const fg = `hsl(${hash}, 45%, 30%)`;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="96" height="96" viewBox="0 0 96 96"><rect width="96" height="96" rx="20" fill="${bg}"/><text x="48" y="55" text-anchor="middle" font-family="Arial, sans-serif" font-size="30" font-weight="700" fill="${fg}">${label}</text></svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

// ================= Ratenvorlage =================

/** index.html:4992 */
export function supplierPlanSummary(s: Supplier): string {
  const rows: SupplierPlanRow[] = Array.isArray(s.paymentPlan) ? s.paymentPlan : [];
  if (rows.length) {
    return rows
      .map((p) =>
        p.pct
          ? `${NUM.format(Number(p.pct) || 0)} %`
          : p.amount
            ? EUR.format(Number(p.amount) || 0)
            : p.label || "Rate",
      )
      .join(" / ");
  }
  // Ungesicherter Nachschlag - siehe Eigenheit 2 im Kopfkommentar. Der Cast ist reine Typarbeit
  // (seit Task 1 ist `paymentTemplate` in `Supplier` `unknown`, siehe `lib/types.ts`): er aendert
  // das Laufzeitverhalten NICHT, `s.paymentTemplate || ''` liefert bei einem wahrhaftigen
  // Nicht-String weiterhin diesen Wert selbst, und die Tabelle wird mit ihm indiziert - genau wie
  // im Original.
  const t = PAYMENT_TEMPLATE_MAP[(s.paymentTemplate || "") as string];
  return t && t.id ? t.label : "Keine Vorlage";
}

// ================= Lieferantenliste (im Original DB-gebunden) =================

/**
 * index.html:4693 - modulinterne Abhaengigkeit von `orderSupplier`. Im Original liest sie
 * `DB.suppliers`, hier kommt die Liste als Argument.
 */
const supplierById = (
  id: unknown,
  suppliers: readonly Supplier[] | null | undefined,
): Supplier | undefined => (suppliers || []).find((s) => s.id === id);

/** index.html:4839 - `DB.suppliers` ist hier das Argument `suppliers`. */
export function allSuppliers(suppliers: readonly Supplier[] | null | undefined): Supplier[] {
  const map = new Map<string, Supplier>();
  for (const s of suppliers || []) {
    const key = supplierKey(s.name);
    if (!key) continue;
    map.set(key, {
      ...s,
      logo: s.logo || "",
      terms: s.terms || "",
      paymentTemplate: s.paymentTemplate || "",
      paymentPlan: Array.isArray(s.paymentPlan) ? s.paymentPlan : [],
      notes: s.notes || "",
    });
  }
  // ABWEICHUNG vom Original (Befund 2, Option B): `String(a.name || '')` statt `(a.name || '')`,
  // dieselbe Behandlung wie in `supplierKey` und aus demselben Grund. Nachgezogen am 2026-08-11
  // (Gesamtreview des Zweigs, Befund I7): der Beschluss lautet „Reparatur in `lib/` ueber die
  // GESAMTE Aufrufkette", und diese Sortierung war die zweite Wurfstelle derselben Kette. Sie
  // vergleicht den ROHEN Namen, nicht den Schluessel - bei ZWEI wahrhaftigen Nicht-String-Namen
  // (42, true, [], {}) rief der Vergleicher `.localeCompare` darauf auf und warf. Zwei fehlerhafte
  // Bestellungen rissen das Board damit weiterhin ganzflaechig ab, obwohl `supplierKey` laengst
  // repariert war. Die Casts von zuvor sind mit `String(...)` entfallen - es wird nicht mehr
  // behauptet, der Wert sei ein String, er wird zu einem gemacht.
  return [...map.values()].sort((a, b) =>
    String(a.name || "").localeCompare(String(b.name || ""), "de"),
  );
}

/**
 * index.html:5002 - `DB.suppliers` (ueber `allSuppliers`) ist hier `suppliers`, `DB.orders` ist
 * `orders`. Nachportiert in Task 13 (Phase 6).
 *
 * Liefert die Namen aller bekannten Lieferanten VEREINIGT mit den auf Bestellungen eingetragenen -
 * eine Bestellung kann einen Lieferanten nennen, der nicht in den Stammdaten steht. Einziger
 * Verwender im Original ist `supplierSelectOptions` (index.html:5017), also die Auswahlliste der
 * Lieferantenfelder; in der Bestellliste ist das die INLINE-Bearbeitung der Spalte „Lieferant"
 * (`orderListEditorCell`, index.html:7669) - ein Schreibpfad und damit Task 14, nicht Task 13.
 *
 * Woertlich uebernommene Eigenheit: sortiert wird mit `localeCompare(..., 'de')` OHNE
 * `sensitivity`-Angabe - anders als in `orderListFacetOptions`, wo Gross-/Kleinschreibung
 * ausdruecklich ignoriert wird.
 */
export function supplierNames(
  suppliers: readonly Supplier[] | null | undefined,
  orders: readonly Order[] | null | undefined,
): string[] {
  const names = new Set<string>();
  for (const s of allSuppliers(suppliers)) {
    const n = ((s.name || "") as string).trim();
    if (n) names.add(n);
  }
  for (const o of orders as readonly Order[]) {
    const n = ((o.supplier || "") as string).trim();
    if (n) names.add(n);
  }
  return [...names].sort((a, b) => a.localeCompare(b, "de"));
}

/** index.html:5038 - `DB.suppliers` ist hier das Argument `suppliers`. */
export function supplierByName(
  name: unknown,
  suppliers: readonly Supplier[] | null | undefined,
): Supplier | undefined {
  const n = supplierKey(name);
  return allSuppliers(suppliers).find((s) => supplierKey(s.name) === n);
}

/** index.html:4759 - `DB.suppliers` ist hier das Argument `suppliers`. */
export function orderSupplier(
  o: Order | null | undefined,
  suppliers: readonly Supplier[] | null | undefined,
): Supplier | null | undefined {
  if (!o) return null;
  if (o.supplierId) {
    const linked = supplierById(o.supplierId, suppliers);
    if (linked) return linked;
  }
  return supplierByName(o.supplier, suppliers);
}

/** index.html:4767 */
export function orderUsesSupplier(
  o: Order | null | undefined,
  s: Supplier | null | undefined,
): boolean {
  if (!o || !s) return false;
  const key = supplierKey(s.name);
  // Der Cast ist reine Typarbeit: der Ausdruck liefert zur Laufzeit immer einen Wahrheitswert - ist
  // `o.supplierId` falsy, faellt `||` auf den rechten, bereits booleschen Teilausdruck durch. Ein
  // `Boolean(...)` um den Ausdruck waere dieselbe Laufzeitwirkung, aber eine Umformulierung.
  return ((o.supplierId && o.supplierId === s.id) ||
    (!!key && supplierKey(o.supplier) === key)) as boolean;
}

/**
 * index.html:5124 - `DB.suppliers` ist hier das Argument `suppliers`.
 *
 * SEIT DEM 2026-08-15 GEWINNT DIE WAEHRUNG AM BELEG. Das Original kannte nur den Umweg ueber den
 * Lieferanten, weil die Bestellung selbst keine Waehrung fuehrte. Seit die Bestellungen live aus
 * JTL kommen, tragen sie `cWaehrungISO` mit - und das ist die Wahrheit fuer genau diese Bestellung,
 * waehrend der Lieferant nur die Vorgabe fuer neue liefert. Ein Lieferant kann seine Waehrung
 * wechseln; alte Belege bleiben in der ihren.
 *
 * Der Fehler, der das sichtbar gemacht hat: `orderSupplier(...)?.eur` liest ein Feld, das der
 * Lieferantenabruf (`GET /api/suppliers/:kLieferant`) gar nicht liefert. `undefined` ist falsy,
 * also fiel JEDE Bestellung auf USD - im Board stand "$ 4.244,16" an einer EUR-Bestellung des
 * deutschen Lieferanten SCHROEDER COSMETICS (live gesehen am 2026-08-15).
 *
 * Der Rueckfall auf den Lieferanten bleibt: eine Bestellung ohne Waehrung am Beleg - etwa ein
 * Entwurf, der JTL noch nicht erreicht hat - verhaelt sich weiter wie bisher.
 */
export function orderCurrency(
  o: Order | null | undefined,
  suppliers: readonly Supplier[] | null | undefined,
): string {
  const amBeleg = typeof o?.waehrung === "string" ? o.waehrung.trim().toUpperCase() : "";
  // Nur die zwei Waehrungen, die das Tool kennt (Anlagemaske, JTL-Schreibpfad). Ein unbekannter
  // Wert faellt bewusst auf den Lieferantenweg zurueck, statt ihn ungeprueft anzuzeigen.
  if (amBeleg === "EUR" || amBeleg === "USD") return amBeleg;
  return orderSupplier(o, suppliers)?.eur ? "EUR" : "USD";
}

// ================= Lager (im Original DB-gebunden) =================

/**
 * index.html:5021 - `DB.orders` ist hier das Argument `orders`.
 *
 * SEIT DEM 19.08.2026 OHNE VERBRAUCHER IN DER OBERFLAECHE: die Lager-Auswahl kommt seither live aus
 * JTL (`GET /api/jtl/warehouses`, `lib/lager.ts#lagerAuswahl`) statt aus `DEFAULT_WAREHOUSES` plus
 * den in Bestellungen benutzten Namen. Diese Funktion bleibt trotzdem UNVERAENDERT stehen - sie ist
 * ein Portierungsbeleg (Kopfkommentar dieser Datei) und wird von `scripts/gen-herkunft.ts`,
 * `test/helpers/configKonstanten.ts` und `test/parity/suppliers.test.ts` weiterhin gegen
 * `index.html` gegengeprueft.
 */
export function warehouseNames(orders: readonly Order[] | null | undefined): string[] {
  const names = new Set(DEFAULT_WAREHOUSES);
  for (const order of orders || []) {
    const name = String(order.warehouse || "").trim();
    if (name) names.add(name);
  }
  return [...names];
}
